import { useTheme } from "styled-components";
import usePricesSocket from "@shared/services/socket/usePricesSocket";
import {
  getAnnotationColor,
  getDistanceFromCurrentPrice,
} from "@shared/features/trades/common/utils";
import { round, toFixed } from "@shared/utils/numbers";
import { ActiveTradeNextOrder, PrecisionType } from "@shared/types";
import { OrderTypes } from "@shared/features/trades/common/types";
import { ICON_TYPES } from "@shared/common/constants";
import { useMemo } from "react";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";

interface UseNextOrderProps {
  symbol?: string;
  exchange?: string;
  nextOrder: ActiveTradeNextOrder | undefined;
  orderType?: OrderTypes;
}

const useNextOrder = ({
  symbol,
  exchange,
  nextOrder,
  orderType,
}: UseNextOrderProps) => {
  const appTheme = useTheme();

  const { socketPrice } = usePricesSocket(symbol, exchange);
  const { precision: pricePrecision } = usePrecisions({
    precisionType: PrecisionType.PRICE,
    symbol,
    exchange,
  });

  const getDistance = () => {
    const socketPricesValue = socketPrice.value;
    let distance = (nextOrder?.distance || 0) * 100;
    if (socketPricesValue && nextOrder)
      distance = getDistanceFromCurrentPrice(
        socketPricesValue,
        nextOrder.price
      );
    return toFixed(distance, 2);
  };

  let pair = (symbol || "").split("/")[1];
  const priceText = round(nextOrder?.price, pricePrecision);
  const color = orderType && getAnnotationColor(orderType, appTheme);
  const distanceText = `${getDistance()}%`;

  const priceProps = useMemo(
    () => ({
      text: priceText,
      coinOrExchangeName: pair,
      iconType: ICON_TYPES.COIN,
    }),
    [priceText, pair]
  );

  return {
    pair,
    priceText,
    pricePrecision,
    priceProps,
    color,
    getDistance,
    distanceText,
  };
};

export default useNextOrder;
